/**
 * RunHeader — the run page header (sheet 20 callout 1, sheet 13 callout 1;
 * mission 6 WP03 T023; FR-005, FR-024, NFR-007; GL.5; C-010).
 *
 * The run label (register 17) as the page's `h1`, the live state as a §2.6
 * status badge (`runStateTone`), and the facts: the input file, the layout
 * version (Path A only), the rule-set version that evaluated the run, the
 * record count, the credits (reserved while live, consumed once terminal) and
 * the times, in `<time dateTime>`. IDs, filenames, versions and counts are
 * mono. The screen states "Times in UTC" once and fills `actions`.
 *
 * mission 7 WP08 T045 (known-followups F2; FR-027; squad U13): Results passes
 * `firedRules`, the number of rules with findings, and the meta line shows it
 * as `fact.firedRules` with the count in mono. Undefined omits the fact.
 */
import type { ReactNode } from 'react'

import { Badge } from '@/components/ui/badge'
import type { Run, RunState } from '@/lib/api/runs'
import { isTerminal } from '@/lib/api/runs'
import { Mono, count, date, isoDateTime, time } from '@/lib/format'
import { PROVISIONAL_COPY, copyText } from '@/lib/provisional-copy'

export type RunTone = { status: 'success' | 'info' | 'neutral' | 'error'; dot: boolean }

/** §2.6: ready success; live states info with a dot; cancelled neutral; rejected and failed error. */
export function runStateTone(state: RunState): RunTone {
  switch (state) {
    case 'ready':
      return { status: 'success', dot: false }
    case 'queued':
    case 'validating':
    case 'generating':
    case 'analyzing':
      return { status: 'info', dot: true }
    case 'cancelled':
      return { status: 'neutral', dot: false }
    case 'rejected':
    case 'failed':
      return { status: 'error', dot: false }
    default:
      return { status: 'info', dot: false }
  }
}

const PATH_LABEL = { generate: 'Generate', analyze: 'Analyze' } as const

function sentence(state: string): string {
  return state.charAt(0).toUpperCase() + state.slice(1)
}

function When({ iso }: { iso: string }) {
  return (
    <time dateTime={isoDateTime(iso)}>
      <Mono>
        {date(iso)} {time(iso)}
      </Mono>
    </time>
  )
}

export interface RunHeaderProps {
  run: Run
  actions?: ReactNode
  /** F2: how many rules fired (rule_counts values above 0); omitted while unknown. */
  firedRules?: number | undefined
}

export function RunHeader({ run, actions, firedRules }: RunHeaderProps) {
  const tone = runStateTone(run.state)
  const terminal = isTerminal(run.state)
  const credits = terminal ? run.credits_consumed : run.credits_reserved
  const facts: Array<[string, ReactNode]> = []
  // mission 6 WP13: handoff from WP03: the fact labels come from the register (fact.*).
  if (run.upload) facts.push([copyText('fact.input'), <Mono>{run.upload.filename}</Mono>])
  if (run.layout_version !== undefined) facts.push([copyText('fact.layout'), <Mono>{run.layout_version}</Mono>])
  facts.push([copyText('fact.ruleSet'), <Mono>{run.rule_set_version}</Mono>])
  // owner decision 2026-09-26 (decision 6): a Path B count is base segments.
  facts.push([copyText(run.path === 'analyze' ? 'fact.baseSegments' : 'fact.records'), <Mono>{count(run.record_count)}</Mono>])
  facts.push([
    copyText('fact.credits'),
    <>
      <Mono>{count(credits)}</Mono> {copyText(terminal ? 'sublabel.consumed' : 'sublabel.reserved')}
    </>,
  ])
  if (firedRules !== undefined) facts.push([copyText('fact.firedRules'), <Mono>{count(firedRules)}</Mono>])
  facts.push([copyText('fact.started'), <When iso={run.started_at} />])
  if (run.completed_at) facts.push([copyText('fact.finished'), <When iso={run.completed_at} />])

  return (
    <header className="flex flex-col gap-3 border-b border-navy-200 pb-4">
      <div className="flex flex-wrap items-center gap-3">
        {/* mission 6 WP13: programmatically focusable, so screens send focus here without writing tabindex (§7.11). */}
        <h1 tabIndex={-1} className="text-h1 text-navy-950">
          <Mono>{PROVISIONAL_COPY.runLabel.render({ runId: run.run_id })}</Mono>
        </h1>
        <span className="text-body text-navy-600">{PATH_LABEL[run.path] ?? run.path}</span>
        <Badge status={tone.status} dot={tone.dot} pulse={tone.dot} data-run-state={run.state}>
          {sentence(run.state)}
        </Badge>
        {actions ? <div className="ml-auto flex items-center gap-3">{actions}</div> : null}
      </div>
      <dl className="flex flex-wrap gap-x-6 gap-y-1 text-body-sm">
        {facts.map(([label, value]) => (
          <div key={label} className="flex items-baseline gap-1.5">
            <dt className="text-navy-600">{label}</dt>
            <dd className="text-navy-950">{value}</dd>
          </div>
        ))}
      </dl>
    </header>
  )
}
